import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import {
  LayerId,
  LayerType,
  SerializedLayers,
  useEventCallback,
} from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import axios from 'axios';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Masonry from 'react-responsive-masonry';
import { useAsync } from 'react-use';
import Photo from './Photo';
import { useTheme } from '@emotion/react';
import { debounce } from 'lodash';
import IconButton from '../../IconButton';
import { useTranslation, Trans } from 'react-i18next';

interface ImageURL {
  url: string;
}

interface ImageItem {
  id: number | string;
  width: number;
  height: number;
  thumb: string;
  image: string;
  name: string;
  username: string;
  photographer_url: string;
  alt: string;
}

const ImageContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const qRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef(false);
  const theme = useTheme();

  const [images, setImages] = useState<ImageItem[]>([]);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const { actions, query, dragNDrop } = useEditor((state) => ({
    dragNDrop: state.dragNDrop,
  }));

  const requestImageURL = async (
    download_location: string
  ): Promise<string> => {
    const params = new URLSearchParams({
      download_location,
      force: 'true',
    }).toString();

    const response = await axios.get<ImageURL>(`images/download/?${params}`);
    const { url } = response.data;
    return url;
  };

  const loadImageList = useEventCallback(async (offset = 0) => {
    dataRef.current = true;
    setIsLoading(true);
    const params = {
      limit: '20',
      page: (images.length % 20) + 1 + '',
      q: keyword,
    };

    const response = await axios.get<ImageItem[]>(
      `/images2?${new URLSearchParams(params).toString()}`
    );

    const { data } = response;

    const nextImages = data.map((img) => {
      const imgUrl = new URL(img.thumb);
      const thumbParams = new URLSearchParams(imgUrl.searchParams);
      thumbParams.set('w', '200');

      // unset the h search param
      thumbParams.delete('h');

      const thumb = `${imgUrl.origin}${
        imgUrl.pathname
      }?${thumbParams.toString()}`;

      return {
        ...img,
        id: `${img.id}`,
        thumb,
      };
    });

    if (offset) {
      setImages((prevState) => {
        prevState.push(...nextImages);
        return prevState;
      });
    } else {
      setImages(nextImages);
    }
    setIsLoading(false);
    if (response.data.length > 0) {
      dataRef.current = false;
    }
  });
  useAsync(async () => {
    await loadImageList(0);
  }, [loadImageList]);

  useEffect(() => {
    const handleLoadMore = async (e: Event) => {
      const node = e.target as HTMLDivElement;
      if (
        node.scrollHeight - node.scrollTop - 80 <= node.clientHeight &&
        !dataRef.current
      ) {
        await loadImageList(images.length);
      }
    };
    scrollRef.current?.addEventListener('scroll', handleLoadMore);
    return () => {
      scrollRef.current?.removeEventListener('scroll', handleLoadMore);
    };
  }, [loadImageList, images]);
  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setKeyword(qRef.current?.value || '');
    setTimeout(async () => {
      await loadImageList(0);
    });
  };
  const addImage = async (item: ImageItem) => {
    actions.addImageLayer(
      { thumb: item.thumb, url: item.image },
      { width: item.width, height: item.height }
    );

    // not sure what the purpose of doing this
    // axios.put(`/images?id=${item.id}`);

    if (isMobile) {
      onClose();
    }
  };

  const handleDrag = (event: React.DragEvent, item: ImageItem) => {
    const { clientX, clientY } = event;
    const pageSize = query.getPageSize();
    const ratio = pageSize.width / pageSize.height;
    const imgRatio = item.width / item.height;
    const w =
      ratio < imgRatio
        ? pageSize.width * 0.8
        : pageSize.height * imgRatio * 0.8;
    const h = w / imgRatio;
    const data: {
      layer: LayerType;
      data: { rootId: LayerId; layers: SerializedLayers };
    } = {
      layer: 'Image',
      data: {
        rootId: item.id as string,
        layers: {
          [item.id]: {
            type: {
              resolvedName: 'ImageLayer',
            },
            props: {
              image: {
                url: item.image,
                thumb: item.thumb,
                boxSize: {
                  width: w,
                  height: h,
                },
                position: {
                  x: 0,
                  y: 0,
                },
                rotate: 0,
              },
              position: {
                x: 0,
                y: 0,
              },
              boxSize: {
                width: w,
                height: h,
              },
              rotate: 0,
            },
            locked: false,
            parent: 'ROOT',
            child: [],
          },
        },
      },
    };
    actions.startDragNDrop(data, { x: clientX, y: clientY });
    event.dataTransfer.clearData('text/plain');
    event.dataTransfer.setData('text/plain', JSON.stringify(data));
    event.dataTransfer.setDragImage(new Image(), 0, 0);
  };

  const translationWithJsx = t('page_editor.image.images_by_unsplash').replace(
    '{unsplash}',
    `
      <a href="https://pexels.com" target="_blank" rel="noopener noreferrer">
        <img src="/pexels-${
          theme.name === 'dark' ? 'white' : 'black'
        }.svg" class="h-4">
      </a>
    `
  );

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: 'flex',
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 50,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          {t('page_editor.general.image')}
        </p>
        <IconButton onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </div>
      <div
        css={(theme) => ({
          flexDirection: 'column',
          overflowY: 'auto',
          display: 'flex',
          flexGrow: 1,
        })}
      >
        <div
          css={(theme) => ({
            borderRadius: 4,
            border: `1px solid ${theme.colors.stroke}`,
            margin: 16,
          })}
        >
          <div
            css={(theme) => ({
              height: 40,
              borderRadius: 4,
              paddingLeft: 12,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            })}
          >
            <div
              css={(theme) => ({
                fontSize: 24,
                marginRight: 8,
                flexShrink: 0,
              })}
            >
              <RiSearchLine size={20} />
            </div>
            <input
              ref={qRef}
              css={(theme) => ({
                width: '100%',
                height: '100%',
                backgroundColor: theme.colors.input,
                paddingLeft: 8,
              })}
              type={'text'}
              onInput={debounce(handleSearch, 400)}
            />
          </div>
        </div>
        <div
          ref={scrollRef}
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            padding: '16px',
            gridGap: 8,
            position: 'relative',
          })}
        >
          <Masonry columnsCount={2} gutter="8px">
            {images.map((item, idx) => (
              <Photo
                key={idx}
                image={item.thumb}
                name={item.name}
                username={item.username}
                onClick={() => {
                  addImage(item);
                }}
                onDragStart={(e) => handleDrag(e, item)}
              />
            ))}
          </Masonry>
          {isLoading && (
            <div
              css={(theme) => ({
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                fontSize: 16,
              })}
            >
              Loading...
            </div>
          )}
        </div>
      </div>

      <div
        css={(theme) => ({
          flexShrink: 0,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 16,
          textAlign: 'center',
        })}
      >
        <p
          css={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            fontSize: 12,
            color: theme.colors.content,
          })}
          dangerouslySetInnerHTML={{ __html: translationWithJsx }}
        ></p>
      </div>
    </div>
  );
};

export default ImageContent;
